import { downloadAdminLicenseReport } from "@/lib/adminLicenseExport";
import { exportLicenseReport } from "@/lib/exportReport";
import { downloadWebhookHealthSnapshot, type HealthWindow } from "@/lib/webhookHealth";
import {
  computeBatchStats,
  downloadAllBatchesReconcile,
  downloadBatchStats,
  downloadCodeList,
} from "@/lib/codePoolExport";
import type { LicenseCode } from "@/pages/admin/tools/hooks/useLicenseTools";
import type { AdminLicense } from "@/pages/admin/hooks/useAdminData";
import type { WebhookDelivery, WebhookEndpoint } from "@/pages/admin/approvals/hooks/useWebhooks";

// 报表内文案的翻译函数（i18n）的宽松类型，供可从报表中心直接生成的报表拼装表头。
export type ReportTranslate = (key: string, options?: Record<string, unknown>) => string;

// 报表中心可用的数据上下文：由报表中心统一加载，供各报表的 run 直接消费。
export interface ReportContext {
  codes: LicenseCode[];
  licenses: AdminLicense[];
  webhookEndpoints: WebhookEndpoint[];
  webhookDeliveries: WebhookDelivery[];
  // 回调端点健康快照使用的时间窗口（24h/7d/30d），由报表中心的选择器统一控制。
  healthWindow: HealthWindow;
  translate: ReportTranslate;
}

// 可在报表中心内直接生成的报表：run 返回导出的记录条数，用于写入导出任务历史。
export interface RunnableReport {
  key: string;
  name: string;
  category: string;
  description: string;
  icon: string;
  run: (ctx: ReportContext) => number | Promise<number>;
  // 预计行数：供批量导出前的范围确认弹窗估算范围（与实际导出口径保持一致）。
  estimate?: (ctx: ReportContext) => number;
}

// 依赖所在页面筛选条件的报表：报表中心只作为统一入口，引导用户前往对应模块导出。
export interface LinkReport {
  key: string;
  name: string;
  category: string;
  description: string;
  icon: string;
  to: string;
}

export const REPORT_CATEGORIES = ["许可证", "激活码", "客户", "审计", "监控", "批量"] as const;

// 分类名称的中文值用于数据分组与配色映射；展示时按 i18n 键翻译，便于海外分发。
export const REPORT_CATEGORY_I18N_KEY: Record<string, string> = {
  许可证: "reports.catLicense",
  激活码: "reports.catCode",
  客户: "reports.catCustomer",
  审计: "reports.catAudit",
  监控: "reports.catMonitor",
  批量: "reports.catBatch",
};

// 可直接生成的报表（报表中心自带数据加载）。
export const RUNNABLE_REPORTS: RunnableReport[] = [
  {
    key: "license-full",
    name: "许可证全量报表",
    category: "许可证",
    description: "导出全部许可证：密钥、产品、版本、状态、席位、激活数与到期时间，含已吊销，适合整体归档。",
    icon: "ri-file-shield-2-line",
    run: (ctx) => downloadAdminLicenseReport(ctx.licenses),
    estimate: (ctx) => ctx.licenses.length,
  },
  {
    key: "license-report",
    name: "授权报表",
    category: "许可证",
    description: "从后端拉取授权明细，含产品、版本、激活与到期信息，适合对外交付或财务核对。",
    icon: "ri-file-list-3-line",
    run: () => exportLicenseReport(),
    estimate: (ctx) => ctx.licenses.length,
  },
  {
    key: "code-pool-all",
    name: "原始激活码全量清单",
    category: "激活码",
    description: "导出全部原始激活码：激活码、适用产品、版本、席位、状态与批次，便于整体归档。",
    icon: "ri-key-2-line",
    run: (ctx) => downloadCodeList(ctx.codes, "全量"),
    estimate: (ctx) => ctx.codes.length,
  },
  {
    key: "code-batch-stats",
    name: "激活码核销统计",
    category: "激活码",
    description: "按批次汇总已核销 / 未核销 / 已停用与核销率，发码对账的第一步。",
    icon: "ri-line-chart-line",
    run: (ctx) => downloadBatchStats(computeBatchStats(ctx.codes)),
    estimate: (ctx) => computeBatchStats(ctx.codes).length,
  },
  {
    key: "code-reconcile-all",
    name: "全部批次对账包",
    category: "激活码",
    description: "把所有批次的「未核销 + 已核销」明细打进同一个 CSV，整批发放与核销现状一次看齐。",
    icon: "ri-file-transfer-line",
    run: (ctx) => downloadAllBatchesReconcile(ctx.codes, computeBatchStats(ctx.codes)).batches,
    estimate: (ctx) => computeBatchStats(ctx.codes).length,
  },
  {
    key: "webhook-health",
    name: "回调端点健康快照",
    category: "监控",
    description: "按报表中心所选时间窗口（24h / 7d / 30d）导出每个回调接收端的成功率 / 平均耗时 / 失败数与最近投递，做可靠性周报，无需跳转回调页。",
    icon: "ri-heart-pulse-line",
    run: (ctx) =>
      downloadWebhookHealthSnapshot(ctx.webhookEndpoints, ctx.webhookDeliveries, ctx.healthWindow, {
        endpoint: ctx.translate("approvals.healthColEndpoint"),
        rate: ctx.translate("approvals.healthColRate"),
        latency: ctx.translate("approvals.healthColLatency"),
        total: ctx.translate("approvals.healthColTotal"),
        failed: ctx.translate("approvals.healthColFailed"),
        last: ctx.translate("approvals.healthColLast"),
        lastTime: ctx.translate("approvals.colDeliveryTime"),
        fileName: ctx.translate("approvals.exportHealthFileName"),
        statusLabel: (status) =>
          status === "success"
            ? ctx.translate("approvals.deliverySuccess")
            : status === "failed"
              ? ctx.translate("approvals.deliveryFailed")
              : status === "retrying"
                ? ctx.translate("approvals.deliveryRetrying")
                : ctx.translate("approvals.deliveryPending"),
      }),
    estimate: (ctx) => ctx.webhookEndpoints.length,
  },
];

// 依赖页面筛选条件的报表：收口为统一入口，点击前往对应模块生成。
export const LINK_REPORTS: LinkReport[] = [
  {
    key: "customer-book",
    name: "客户台账",
    category: "客户",
    description: "客户全貌清单（联系方式、风险等级、负责人），支持在客户管理页按筛选导出。",
    icon: "ri-group-line",
    to: "/admin/customers",
  },
  {
    key: "customer-risk",
    name: "风险客户名单",
    category: "客户",
    description: "识别出的风险客户名单，供批量指派负责人与催办。",
    icon: "ri-alarm-warning-line",
    to: "/admin/customers",
  },
  {
    key: "customer-followup",
    name: "客服跟进清单 / 跟进看板周报",
    category: "客户",
    description: "跟进中的客户清单与负责人工作量周报，主管复盘用。",
    icon: "ri-user-follow-line",
    to: "/admin/customers",
  },
  {
    key: "audit-log",
    name: "审计日志",
    category: "审计",
    description: "全量操作留痕，支持按操作人 / 时间 / 级别筛选后导出。",
    icon: "ri-file-history-line",
    to: "/admin/audit",
  },
  {
    key: "audit-ranking",
    name: "操作排行 / 批次设备清单",
    category: "审计",
    description: "操作人排行榜与批次设备明细，用于月度归档与设备回看。",
    icon: "ri-bar-chart-grouped-line",
    to: "/admin/audit",
  },
  {
    key: "monitor-alert",
    name: "监控告警日志",
    category: "监控",
    description: "阈值触发的告警日志清单，便于运维复盘与容量规划。",
    icon: "ri-line-chart-line",
    to: "/admin/monitor",
  },
  {
    key: "license-devices",
    name: "设备清单 / 已核销许可证清单",
    category: "许可证",
    description: "按许可证导出激活设备明细，或导出已核销的许可证清单。",
    icon: "ri-computer-line",
    to: "/licenses",
  },
];